/* eslint-disable */
/**
 * TODO - preamble
 */
import React from "react";
import { FormattedHTMLMessage } from "react-intl";
import { produce } from "immer";

import { useMessage } from "@/components/Message";

import {
  VerificationService,
  StudentPersonalInfoViewModel,
  Organization,
  StudentPersonalInfoResponse,
  CustomFormFieldConfig,
  type FieldId,
  type FormSelectChoice,
  type StudentStatusSelection,
} from "@/lib/types/types";

import { StudentStatusDefaultMessagesEnum, VerificationStepsEnum } from "@/lib/types/runtimeTypes";
import { hasFailedInstantMatch } from "@/lib/VerificationService/VerificationServiceHelpers";
import {
  getFieldConfigByFieldId,
  handleEmailOnKeyDown,
  updateFieldValidationErrorsByFieldId,
  updateViewModelOrganization,
} from "@/lib/utils/stepComponentHelpers/stepComponentHelpers";

import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { CollegeNameComponent as CollegeName } from "../FormFields/CollegeName/CollegeNameComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { getOptions } from "../../options/options";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { ExtraMessage } from "../ExtraMessage";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";
import { useFlag } from "../FeatureFlags/hooks";
import { CVECNumberComponent } from "../FormFields/CVECNumber/CVECNumberComponent";
import { CaptchaComponent } from "../FormFields/Captcha/CaptchaComponent";
import { StatusComponent as Status } from "../FormFields/Status/StatusComponent";

// Graduation status dropdown is only Student/Graduate options, not the raw `availableStatuses`
const STATUS_SELECTIONS = Object.keys(StudentStatusDefaultMessagesEnum) as StudentStatusSelection[];

interface StepStudentPersonalInfoComponentProps {
  verificationService: VerificationService;
}

export const StepStudentPersonalInfoComponent = ({
  verificationService,
}: StepStudentPersonalInfoComponentProps) => {
  const { msg } = useMessage();
  const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
  const { fieldValidationErrors } = verificationService;
  const verificationResponse =
    verificationService.verificationResponse as StudentPersonalInfoResponse;
  const options = getOptions();

  // #region include-cvec-field-france-student
  const showCvecInputVariation = useFlag("include-cvec-field-france-student");
  const showCvecInput =
    showCvecInputVariation !== "control" &&
    showCvecInputVariation !== "default" &&
    viewModel?.countryChoice?.value === "FR";
  // #endregion include-cvec-field-france-student

  // The "Graduation Status" input applies only when the program offers a genuine
  // Student or Graduate choice: GRADUATE and at least one non-graduate student status
  // The "Student" option includes all program configured non-graduate statuses
  const availableStatuses = verificationResponse.availableStatuses ?? [];
  const showGraduateStatusInput =
    availableStatuses.includes("GRADUATE") && availableStatuses.some((s) => s !== "GRADUATE");

  React.useEffect(() => {
    let skip: FieldId[] = (verificationService.viewModel?.fieldsToSkipValidation || []).filter(
      (id) => id !== "cvecNumber" && id !== "status",
    );
    if (!showCvecInput) {
      // Don't validate CVEC number if country is not France
      skip = [...skip, "cvecNumber"];
    }
    if (!showGraduateStatusInput) {
      // Only require a graduation status when the program offers the Student/Graduate choice.
      skip = [...skip, "status"];
    }
    updateStudentViewModel("fieldsToSkipValidation", skip);
  }, [showCvecInput, showGraduateStatusInput]);

  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

  const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: any) => {
    const nextState: StudentPersonalInfoViewModel = produce(
      viewModel,
      (draft: StudentPersonalInfoViewModel) => {
        (draft[key] as any) = value;
      },
    );
    verificationService.updateViewModel(nextState);
  };

  const locale = viewModel.localeChoice.value;

  const getStatusChoice = (status?: StudentStatusSelection): FormSelectChoice =>
    status
      ? {
          value: status,
          label: msg({ id: status }),
        }
      : { value: "", label: "" };

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="student-personal-info"
      />
      <ChangeLocaleV2Component verificationService={verificationService} />
      <CountryComponentWrapper
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("country")?.disabled}
      />
      {showGraduateStatusInput && (
        <Status
          segmentId="student"
          label={<FormattedHTMLMessage id="graduationStatus" defaultMessage="Graduation Status" />}
          value={getStatusChoice(viewModel.status)}
          isErrored={!!fieldValidationErrors.status}
          options={STATUS_SELECTIONS.map(getStatusChoice)}
          disabled={getFieldConfigByFieldId("status")?.disabled}
          onChange={(status: FormSelectChoice) => {
            const value = status ? (status.value as StudentStatusSelection) : undefined;
            updateStudentViewModel("status", value);
            updateFieldValidationErrorsByFieldId("status", value ?? "", verificationService);
          }}
        />
      )}
      <CollegeName
        value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.organization}
        isRequired
        disabled={getFieldConfigByFieldId("organization")?.disabled}
        onChange={(choice: Organization) => {
          updateViewModelOrganization(choice, verificationService);
          updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
        }}
      />
      {showCvecInput && (
        <CVECNumberComponent
          isRequired={getFieldConfigByFieldId("cvecNumber")?.requireIfOptional}
          value={viewModel.cvecNumber}
          isErrored={!!fieldValidationErrors.cvecNumber}
          disabled={getFieldConfigByFieldId("cvecNumber")?.disabled}
          onChange={(newValue) => {
            updateStudentViewModel("cvecNumber", newValue);
            updateFieldValidationErrorsByFieldId("cvecNumber", newValue, verificationService);
          }}
        />
      )}
      <div className="sid-names-explanation sid-l-space-top-md">
        <FormattedHTMLMessage
          id="step.personalInfo.nameInstructions"
          defaultMessage="Personal or school email is fine"
        />
      </div>
      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          disabled={getFieldConfigByFieldId("firstName")?.disabled}
          onChange={(newValue) => {
            updateStudentViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />

        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          disabled={getFieldConfigByFieldId("lastName")?.disabled}
          onChange={(newValue) => {
            updateStudentViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>
      <BirthDate
        value={viewModel.birthDate}
        errorId={fieldValidationErrors.birthDate}
        locale={locale}
        isRequired
        disabled={getFieldConfigByFieldId("birthDate")?.disabled}
        onChange={(newValue) => {
          updateStudentViewModel("birthDate", newValue);
          updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
        }}
      />
      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={
          <FormattedHTMLMessage
            id="emailExplanation"
            defaultMessage="Personal or school email is fine"
          />
        }
        onChange={(newValue) => {
          updateStudentViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
        disabled={getFieldConfigByFieldId("email")?.disabled}
      />
      {verificationService.programTheme.isSmsNotifierConfigured ||
      verificationService.programTheme.smsLoopEnabled ? (
        <PhoneNumber
          isRequired={!!verificationService.programTheme.smsLoopEnabled}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
          onChange={(newValue) => {
            updateStudentViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
        />
      ) : null}
      {(options.customFormFields || []).map((config: CustomFormFieldConfig) => (
        <FormFieldCustom config={config} verificationService={verificationService} />
      ))}{" "}
      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
        onChange={(newValue) => {
          updateStudentViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
      />
      <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />
      <CaptchaComponent
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.captchaToken}
      />
      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectStudentPersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        failedInstantMatch={failedInstantMatch}
        buttonText={
          <FormattedHTMLMessage
            id="step.personalInfo.submitButtonText"
            defaultMessage="Verify My Student Status"
          />
        }
      />
    </PersonalInfoWrapper>
  );
};
